// Anniversary API — typed wrappers for /api/v1/anniversaries/* (W3.1 §4).
//
// Mirrors the album.ts pattern: openapi-fetch + ApiError helper + per-endpoint
// typed payloads. Types are aliases onto the generated contract where
// identical to it (`schema.ts`); the rest are still hand-written (per
// frontend/AGENTS.md §5), each with a recorded reason in
// `schema-parity.test.ts`.

import { api as openapi } from "./client";
import { ApiError } from "./pair";
import { assertResponseBody } from "./emptyBody";
import type {
  AnniversaryTrashItem as SchemaAnniversaryTrashItem,
  ClassifyCategoryRequest as SchemaClassifyCategoryRequest,
} from "./schema";
import { API_REQUEST_FAILED } from "../i18n/errors";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** openapi-fetch reads query params from `params.query` — see diary.ts. */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Without it `apiError` can only try to re-read a body the client consumed.
  return { response: result.response, data, error: (result as { error?: unknown }).error };
}

async function apiError(
  res: { response: Response; data: unknown; error?: unknown },
  fallbackCode: string,
): Promise<ApiError> {
  // W39.81 — read openapi-fetch's already-parsed body first. The `Response` in
  // `res.response` has had its body consumed by the client
  // (`dist/index.js:244`), so `clone().json()` cannot read it and every
  // anniversary error fell through to the fallback. See `album.ts` for the full
  // account, including why W39.79's "repo-wide" claim was five modules short.
  let body: unknown = res.error ?? null;
  if (body === null) {
    try {
      body = await res.response.clone().json();
    } catch {
      body = null;
    }
  }
  const err = (
    body as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null
  )?.error;
  return new ApiError(
    res.response.status,
    err?.code ?? fallbackCode,
    err?.message ?? API_REQUEST_FAILED,
  );
}

// ----- Schemas (mirror backend app/modules/anniversary/schemas.py) ----------

export type AnniversaryCategory =
  "birthday" | "festival" | "anniversary" | "date" | "travel" | "custom" | "personal";

export interface UserChip {
  id: string;
  display_name: string;
  avatar_url: string | null;
}

export interface AnniversaryRead {
  id: string;
  pair_id: string;
  author: UserChip | null;
  title: string;
  description: string | null;
  /** ISO 8601 date (YYYY-MM-DD). */
  date: string;
  category: AnniversaryCategory | string;
  custom_category_label: string | null;
  is_recurring: boolean;
  is_anchor_anniversary: boolean;
  is_auto_generated: boolean;
  is_private: boolean;
  reminder_days: number[];
  reminder_time: string | null;
  days_until_next: number | null;
  next_occurrence_date: string | null;
  cover_media_url: string | null;
  derived_diary_count: number;
  created_at: string;
  updated_at: string;
}

/** Slimmer shape used by `AnniversaryListItem` for siblingsNav helpers. */
export type AnniversaryListItem = AnniversaryRead;

export interface AnniversaryListResponse {
  items: AnniversaryRead[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface AnniversaryCreateInput {
  title: string;
  description?: string | null;
  /** ISO 8601 date (YYYY-MM-DD). */
  date: string;
  category: string;
  custom_category_label?: string | null;
  is_recurring?: boolean;
  reminder_days?: number[];
  reminder_time?: string | null;
  is_private?: boolean;
}

export interface AnniversaryUpdateInput {
  title?: string | null;
  description?: string | null;
  date?: string | null;
  category?: string | null;
  custom_category_label?: string | null;
  is_recurring?: boolean | null;
  reminder_days?: number[] | null;
  reminder_time?: string | null;
  is_private?: boolean | null;
}

export interface AnniversaryListOpts {
  cursor?: string | null;
  limit?: number;
  category?: string[];
  is_recurring?: boolean;
  is_private?: boolean;
  is_anchor?: boolean;
  author_id?: string | null;
  /** Date window filters for hero segment 3 (upcoming list). */
  date_from?: string | null;
  date_to?: string | null;
}

export interface AnniversaryTimelineEvent {
  id: string;
  kind: "anniversary" | "milestone";
  title: string;
  date: string;
  category: string | null;
  days_until: number;
  is_today: boolean;
  is_anchor: boolean;
  author: UserChip | null;
  thumbnail_url: string | null;
  /**
   * W45.0 `ADR-0114` — the parent anniversary, set **only** for
   * `kind: "milestone"`.
   *
   * A milestone's `id` is its own `AnniversaryMilestone` primary key, and
   * `/anniversaries/{milestone_id}` has no route. Without this field the only
   * correct behaviour was to make milestones non-tappable, which is what shipped
   * and which read as a design decision rather than the technical limitation it
   * was. Null for a real anniversary, where `id` is already the destination.
   */
  anniversary_id?: string | null;
}

/**
 * 刻意手寫：`events` 是本地的 `AnniversaryTimelineEvent`，其 `category` /
 * `author` / `thumbnail_url` 都是 required-nullable；contract 的
 * `TimelineEvent` 三個都改成 optional。alias 外層會在無聲中把 timeline 的
 * 每次讀取都放寬。見 `schema-parity.test.ts`。
 */
export interface AnniversaryYearSection {
  year: number;
  label: string;
  events: AnniversaryTimelineEvent[];
  is_now: boolean;
  is_past_section: boolean;
  is_future_section: boolean;
  is_collapsed_default: boolean;
}

export interface AnniversaryTimelineResponse {
  years: AnniversaryYearSection[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface AnniversaryTimelineOpts {
  future_years?: number;
  past_years?: number;
  category?: string[];
}

export interface AnniversaryTodayItem {
  id: string;
  title: string;
  category: string;
  is_anchor: boolean;
  days_until: number;
  thumbnail_url: string | null;
}

/**
 * 刻意手寫：contract 的 `TodayItem` 把 `thumbnail_url` 標成 optional，而
 * hero 區塊會把整列直接賦給本地賴以 render 的 `AnniversaryTodayItem[]`。
 * 見 `schema-parity.test.ts`。
 */
export interface AnniversaryTodayResponse {
  items: AnniversaryTodayItem[];
  refreshed_at: string;
}

export interface AnniversaryNextItem {
  id: string;
  title: string;
  category: string;
  is_anchor: boolean;
  days_until: number;
  next_occurrence_date: string;
  thumbnail_url: string | null;
}

/**
 * 刻意手寫：contract 把 `item` 與 `thumbnail_url` 都標成 optional，而 hero
 * 兩者都當成一定有值來讀。見 `schema-parity.test.ts`。
 */
export interface AnniversaryNextResponse {
  item: AnniversaryNextItem | null;
}

export interface AnniversaryMilestoneRead {
  id: string;
  pair_id: string;
  anniversary_id: string;
  kind: string;
  threshold_value: number;
  status: string;
  achieved_at: string | null;
  missed_at: string | null;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface AnniversaryMilestoneListResponse {
  items: AnniversaryMilestoneRead[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface AnniversaryActivityRead {
  id: string;
  anniversary_id: string;
  actor: UserChip | null;
  action: string;
  label_zh: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface AnniversaryActivityListResponse {
  items: AnniversaryActivityRead[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface AnniversaryDiaryRef {
  id: string;
  title: string | null;
  date: string;
  excerpt: string | null;
}

/**
 * 刻意手寫：contract 的 `AnniversaryDiaryRef` 把 `title` 與 `excerpt` 標成
 * optional，而日記選擇器兩個都會讀。見 `schema-parity.test.ts`。
 */
export interface AnniversaryDiaryListResponse {
  items: AnniversaryDiaryRef[];
  total: number;
}

export type AnniversaryClassifyBand = "high" | "mid" | "low" | "reject";

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type AnniversaryClassifyInput = SchemaClassifyCategoryRequest;

export interface AnniversaryClassifyResponse {
  category: string;
  confidence: number;
  probabilities: Record<string, number>;
  band: AnniversaryClassifyBand;
}

// ----- Endpoints -----------------------------------------------------------

/** GET /api/v1/anniversaries — list pair anniversaries. */
export async function listAnniversaries(
  opts: AnniversaryListOpts = {},
): Promise<AnniversaryListResponse> {
  const params: Record<string, unknown> = {
    limit: opts.limit ?? 50,
  };
  if (opts.cursor) params["cursor"] = opts.cursor;
  if (opts.category && opts.category.length > 0) params["category"] = opts.category;
  if (typeof opts.is_recurring === "boolean") params["is_recurring"] = opts.is_recurring;
  if (typeof opts.is_private === "boolean") params["is_private"] = opts.is_private;
  if (typeof opts.is_anchor === "boolean") params["is_anchor"] = opts.is_anchor;
  if (opts.author_id) params["author_id"] = opts.author_id;
  if (opts.date_from) params["date_from"] = opts.date_from;
  if (opts.date_to) params["date_to"] = opts.date_to;

  const res = await call<AnniversaryListResponse>("GET", "/api/v1/anniversaries", {
    params: { query: params },
  });
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_LIST_FAILED");
  return (res.data ?? { items: [], next_cursor: null, has_more: false }) as AnniversaryListResponse;
}

/** POST /api/v1/anniversaries — create. */
export async function createAnniversary(payload: AnniversaryCreateInput): Promise<AnniversaryRead> {
  const res = await call<AnniversaryRead>("POST", "/api/v1/anniversaries", { body: payload });
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_CREATE_FAILED");
  return res.data as AnniversaryRead;
}

/** GET /api/v1/anniversaries/{id}. */
export async function getAnniversary(id: string): Promise<AnniversaryRead> {
  const res = await call<AnniversaryRead>("GET", `/api/v1/anniversaries/${id}`);
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_GET_FAILED");
  return res.data as AnniversaryRead;
}

/** PATCH /api/v1/anniversaries/{id}. */
export async function updateAnniversary(
  id: string,
  payload: AnniversaryUpdateInput,
): Promise<AnniversaryRead> {
  const res = await call<AnniversaryRead>("PATCH", `/api/v1/anniversaries/${id}`, {
    body: payload,
  });
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_UPDATE_FAILED");
  return res.data as AnniversaryRead;
}

/** DELETE /api/v1/anniversaries/{id} — soft-delete (anchor needs confirm=true). */
export async function softDeleteAnniversary(
  id: string,
  opts: { confirm?: boolean } = {},
): Promise<void> {
  const params: Record<string, unknown> = {};
  if (opts.confirm) params["confirm"] = true;
  const res = await call<unknown>("DELETE", `/api/v1/anniversaries/${id}`, {
    expectBody: false,

    params: { query: params },
  });
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_DELETE_FAILED");
}

/** POST /api/v1/anniversaries/{id}/restore — within the 30-day window.
 *
 * W39.54: the window was 7 and is now 30, matching the retention job that
 * hard-deletes the row. See `wishlist.ts` for why the two halves must be one
 * number. */
export async function restoreAnniversary(id: string): Promise<AnniversaryRead> {
  const res = await call<AnniversaryRead>("POST", `/api/v1/anniversaries/${id}/restore`);
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_RESTORE_FAILED");
  return res.data as AnniversaryRead;
}

// --- Trash (W39.54) ---------------------------------------------------------
//
// The twin of the wishlist trash. `title` here (anniversaries name their field
// `title`; wishes name theirs `name`) and `days_left` server-computed — see
// `wishlist.ts` for why the countdown is not recomputed here.

// Contract alias — all five fields are required primitives on both sides
// (see `schema-parity.test.ts` §VERIFIED_IDENTICAL, which fails to compile if
// that stops being true). The *camelCase* trash view model that genuinely has
// no contract counterpart is `AlbumTrashItem` in `album.ts`, not this one.
export type AnniversaryTrashItem = SchemaAnniversaryTrashItem;

export async function listAnniversaryTrash(): Promise<AnniversaryTrashItem[]> {
  const res = await call<{ items: AnniversaryTrashItem[] }>("GET", "/api/v1/anniversaries/trash");
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_TRASH_FAILED");
  return res.data?.items ?? [];
}

/** `DELETE /api/v1/anniversaries/{id}/permanent` — the irreversible one. */
export async function purgeAnniversary(id: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/anniversaries/${id}/permanent`, {
    expectBody: false,
  });
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_PURGE_FAILED");
}

/** GET /api/v1/anniversaries/{id}/milestones. */
export async function listMilestones(
  id: string,
  opts: { status?: string; cursor?: string | null; limit?: number } = {},
): Promise<AnniversaryMilestoneListResponse> {
  const params: Record<string, unknown> = { limit: opts.limit ?? 50 };
  if (opts.status) params["status"] = opts.status;
  if (opts.cursor) params["cursor"] = opts.cursor;
  const res = await call<AnniversaryMilestoneListResponse>(
    "GET",
    `/api/v1/anniversaries/${id}/milestones`,
    { params: { query: params } },
  );
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_MILESTONES_FAILED");
  return (res.data ?? {
    items: [],
    next_cursor: null,
    has_more: false,
  }) as AnniversaryMilestoneListResponse;
}

/** GET /api/v1/anniversaries/{id}/activity — audit log. */
export async function listActivity(
  id: string,
  opts: { cursor?: string | null; limit?: number } = {},
): Promise<AnniversaryActivityListResponse> {
  const params: Record<string, unknown> = { limit: opts.limit ?? 50 };
  if (opts.cursor) params["cursor"] = opts.cursor;
  const res = await call<AnniversaryActivityListResponse>(
    "GET",
    `/api/v1/anniversaries/${id}/activity`,
    { params: { query: params } },
  );
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_ACTIVITY_FAILED");
  return (res.data ?? {
    items: [],
    next_cursor: null,
    has_more: false,
  }) as AnniversaryActivityListResponse;
}

/** GET /api/v1/anniversaries/{id}/diaries — derived diaries. */
export async function getAnniversaryDiaries(id: string): Promise<AnniversaryDiaryListResponse> {
  const res = await call<AnniversaryDiaryListResponse>(
    "GET",
    `/api/v1/anniversaries/${id}/diaries`,
  );
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_DIARIES_FAILED");
  return (res.data ?? { items: [], total: 0 }) as AnniversaryDiaryListResponse;
}

/** GET /api/v1/anniversaries/timeline — 3-section timeline. */
export async function getTimeline(
  opts: AnniversaryTimelineOpts = {},
): Promise<AnniversaryTimelineResponse> {
  const query: Record<string, unknown> = {
    future_years: opts.future_years ?? 5,
    past_years: opts.past_years ?? 5,
  };
  if (opts.category && opts.category.length > 0) query["category"] = opts.category;
  const res = await call<AnniversaryTimelineResponse>("GET", "/api/v1/anniversaries/timeline", {
    params: { query },
  });
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_TIMELINE_FAILED");
  return (res.data ?? {
    years: [],
    next_cursor: null,
    has_more: false,
  }) as AnniversaryTimelineResponse;
}

/** GET /api/v1/anniversaries/today — today's events. */
export async function getToday(): Promise<AnniversaryTodayResponse> {
  const res = await call<AnniversaryTodayResponse>("GET", "/api/v1/anniversaries/today");
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_TODAY_FAILED");
  return (res.data ?? {
    items: [],
    refreshed_at: new Date().toISOString(),
  }) as AnniversaryTodayResponse;
}

/** GET /api/v1/anniversaries/next — next upcoming. */
export async function getNext(): Promise<AnniversaryNextResponse> {
  const res = await call<AnniversaryNextResponse>("GET", "/api/v1/anniversaries/next");
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_NEXT_FAILED");
  return (res.data ?? { item: null }) as AnniversaryNextResponse;
}

/**
 * POST /api/v1/anniversary/classify_category — Laya SUGGEST (singular
 * prefix preserved for backward compatibility per W1.0-laya).
 */
export async function classifyCategory(
  payload: AnniversaryClassifyInput,
): Promise<AnniversaryClassifyResponse> {
  const res = await call<AnniversaryClassifyResponse>(
    "POST",
    "/api/v1/anniversary/classify_category",
    { body: payload },
  );
  if (!res.response.ok) throw await apiError(res, "ANNIVERSARY_CLASSIFY_FAILED");
  return res.data as AnniversaryClassifyResponse;
}
